import {useCallback, useMemo, useState} from 'react'
import {Pressable, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import debounce from 'lodash.debounce'

import {
  IDENTITY_LABEL_DISPLAY_NAMES,
  IDENTITY_LABELS,
  type IdentityLabel,
} from '#/lib/moderation'
import {Nux, useSaveNux} from '#/state/queries/nuxs'
import {
  useUpdateUserPreferencesMutation,
  useUserPreferencesQuery,
} from '#/state/queries/user-preferences'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon} from '#/components/Button'
import * as Toggle from '#/components/forms/Toggle'
import {
  ChevronBottom_Stroke2_Corner0_Rounded as ChevronDown,
  ChevronTop_Stroke2_Corner0_Rounded as ChevronUp,
} from '#/components/icons/Chevron'
import {CircleInfo_Stroke2_Corner0_Rounded as InfoIcon} from '#/components/icons/CircleInfo'
import {TimesLarge_Stroke2_Corner0_Rounded as X} from '#/components/icons/Times'
import * as Prompt from '#/components/Prompt'
import {Text} from '#/components/Typography'

export function ExploreInterestsCard() {
  const t = useTheme()
  const {_} = useLingui()
  const {mutateAsync: saveNux} = useSaveNux()
  const trendingPrompt = Prompt.usePromptControl()
  const [closing, setClosing] = useState(false)
  const [infoExpanded, setInfoExpanded] = useState(false)

  const {data: userPrefs} = useUserPreferencesQuery()
  const {mutate: updatePreferences} = useUpdateUserPreferencesMutation()

  const [localSelections, setLocalSelections] = useState<string[]>([])
  const [hasInitialized, setHasInitialized] = useState(false)

  // Initialize local state from server data
  if (userPrefs && !hasInitialized) {
    setLocalSelections(userPrefs.showMeIdentities)
    setHasInitialized(true)
  }

  // Debounced save function
  const debouncedSave = useMemo(
    () =>
      debounce((identities: IdentityLabel[]) => {
        updatePreferences(identities)
      }, 1500),
    [updatePreferences],
  )

  const onChangeIdentities = useCallback(
    (values: string[]) => {
      setLocalSelections(values)
      debouncedSave(values as IdentityLabel[])
    },
    [debouncedSave],
  )

  const onClose = () => {
    trendingPrompt.open()
  }

  const onConfirmClose = () => {
    setClosing(true)
    saveNux({
      id: Nux.ExploreInterestsCard,
      completed: true,
      data: undefined,
    }).catch(() => {})
  }

  const toggleInfoExpanded = () => {
    setInfoExpanded(prev => !prev)
  }

  return closing ? null : (
    <>
      <Prompt.Basic
        control={trendingPrompt}
        title={_(msg`Dismiss preferences`)}
        description={_(
          msg`You can adjust your preferences at any time from settings.`,
        )}
        confirmButtonCta={_(
          msg({
            message: `OK`,
            comment: `Confirm button text.`,
          }),
        )}
        onConfirm={onConfirmClose}
      />

      <View style={[a.pb_2xs]}>
        <View
          style={[
            a.p_lg,
            a.border_b,
            a.gap_md,
            t.atoms.border_contrast_medium,
          ]}>
          <View style={[a.flex_row, a.gap_sm, a.align_center]}>
            <Text style={[a.text_xl, a.font_bold, a.leading_tight]}>
              <Trans>Show Me</Trans>
            </Text>
          </View>

          <Text style={[a.text_sm, a.leading_snug, t.atoms.text_contrast_medium]}>
            <Trans>Select one or more</Trans>
          </Text>

          <Toggle.Group
            values={localSelections}
            onChange={onChangeIdentities}
            label={_(msg`Select identity preferences`)}>
            <View style={[a.flex_row, a.flex_wrap, a.gap_sm]}>
              {IDENTITY_LABELS.map(identity => (
                <Toggle.Item
                  key={identity}
                  name={identity}
                  label={IDENTITY_LABEL_DISPLAY_NAMES[identity]}>
                  <IdentityButton identity={identity} />
                </Toggle.Item>
              ))}
            </View>
          </Toggle.Group>

          {/* Expandable info section */}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={_(msg`What do these terms mean?`)}
            accessibilityHint={_(msg`Tap to expand or collapse definitions`)}
            onPress={toggleInfoExpanded}
            style={[
              a.flex_row,
              a.align_center,
              a.gap_sm,
              a.py_sm,
            ]}>
            <InfoIcon size="sm" fill={t.atoms.text_contrast_medium.color} />
            <Text style={[a.text_sm, t.atoms.text_contrast_medium, a.flex_1]}>
              <Trans>What do these terms mean?</Trans>
            </Text>
            {infoExpanded ? (
              <ChevronUp size="sm" fill={t.atoms.text_contrast_medium.color} />
            ) : (
              <ChevronDown size="sm" fill={t.atoms.text_contrast_medium.color} />
            )}
          </Pressable>

          {infoExpanded && (
            <View
              style={[
                a.p_md,
                a.rounded_sm,
                a.gap_sm,
                t.atoms.bg_contrast_25,
              ]}>
              <View style={[a.gap_xs]}>
                <Text style={[a.text_sm]}>
                  <Text style={[a.font_bold]}>Cis</Text>
                  <Trans> = gender matches sex assigned at birth</Trans>
                </Text>
              </View>
              <View style={[a.gap_xs]}>
                <Text style={[a.text_sm]}>
                  <Text style={[a.font_bold]}>Trans</Text>
                  <Trans> = gender differs from sex assigned at birth</Trans>
                </Text>
              </View>
              <View style={[a.gap_xs]}>
                <Text style={[a.text_sm]}>
                  <Text style={[a.font_bold]}>Non-binary</Text>
                  <Trans> = not exclusively masculine or feminine</Trans>
                </Text>
              </View>
            </View>
          )}

          <Button
            label={_(msg`Hide this card`)}
            size="small"
            variant="ghost"
            color="secondary"
            shape="round"
            onPress={onClose}
            style={[
              a.absolute,
              {top: a.pt_sm.paddingTop, right: a.pr_sm.paddingRight},
            ]}>
            <ButtonIcon icon={X} size="md" />
          </Button>
        </View>
      </View>
    </>
  )
}

function IdentityButton({identity}: {identity: IdentityLabel}) {
  const t = useTheme()
  const ctx = Toggle.useItemContext()

  return (
    <View
      style={[
        a.rounded_full,
        a.py_sm,
        a.px_lg,
        t.atoms.bg_contrast_50,
        ctx.hovered && t.atoms.bg_contrast_100,
        ctx.focused && t.atoms.bg_contrast_100,
        ctx.pressed && t.atoms.bg_contrast_100,
        ctx.selected && t.atoms.bg_contrast_900,
        ctx.selected &&
          (ctx.hovered || ctx.focused || ctx.pressed) &&
          t.atoms.bg_contrast_975,
      ]}>
      <Text
        selectable={false}
        style={[
          a.text_sm,
          a.font_bold,
          {color: t.palette.contrast_900},
          ctx.selected && t.atoms.text_inverted,
        ]}>
        {IDENTITY_LABEL_DISPLAY_NAMES[identity]}
      </Text>
    </View>
  )
}
